import type { WatchedSecondsData, WatchedSecondsSlideData } from "../../../types/telemetry";
import { playerWatched } from "../../apollo/typePolicies/LocalStorage";
import type { Telemetry } from "../../telemetry/TelemetryModel";
import { isExpectedMediaPlaybackRejection } from "../../util/mediaPlaybackErrors";
import type { PlayerWithMediaState } from "./playerTypes";

export type CloudflareStreamSource = {
  customerCode: string;
  token: string;
};

/**
 * Helper function to update player watched data with telemetry payload
 */
export const updatePlayerWatchedPayload = (
  currentData: WatchedSecondsData,
  slideUuid: string,
  payload: Telemetry | null,
  updatedSeconds: number[],
): void => {
  if (payload) {
    const updatedData = {
      ...currentData,
      [slideUuid]: {
        payload, // Always update with latest payload
        seconds: updatedSeconds,
      },
    };
    playerWatched(updatedData);
  } else {
    console.error("Failed to create telemetry payload for slide tracking", {
      slideUuid,
      second: updatedSeconds[updatedSeconds.length - 1],
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Helper function to update stored media time in local storage
 */
export const setStoredMediaTime = (
  currentData: WatchedSecondsData,
  slideUuid: string,
  existingSlideData: WatchedSecondsSlideData,
  playerRef: React.RefObject<PlayerWithMediaState | null>,
): void => {
  // Get current media_time from video
  const video = playerRef.current;
  const currentMediaTime = video?.currentTime ? Math.floor(video.currentTime) : 0;

  // Update payload with current media_time but keep everything else the same
  const updatedPayload = {
    ...existingSlideData.payload,
    media_time: currentMediaTime,
  };

  const updatedData = {
    ...currentData,
    [slideUuid]: {
      ...existingSlideData,
      payload: updatedPayload, // Update payload with current media_time
    },
  };

  playerWatched(updatedData);
};

export const formatTimestamp = (seconds: number): string => {
  const setValue = (value: number) => value.toString().padStart(2, "0");
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  return `${setValue(hours)}:${setValue(minutes)}:${setValue(secs)}`;
};

export const updateStoredMediaTime = (
  slideUuid: string,
  playerRef: React.RefObject<PlayerWithMediaState | null>,
): void => {
  const currentData = playerWatched();
  const existingSlideData = currentData[slideUuid];

  if (existingSlideData) {
    setStoredMediaTime(currentData, slideUuid, existingSlideData, playerRef);
  }
};

export const executeWhenPlayerReady = (
  playerRef: React.RefObject<PlayerWithMediaState | null>,
  onPlayerReady: (currentSecond: number) => void,
): number | undefined => {
  if (playerRef.current?.duration && isFinite(playerRef.current.duration)) {
    const currentSecond = Math.floor(playerRef.current.currentTime);
    onPlayerReady(currentSecond);
    return currentSecond;
  }
  return undefined;
};

export const parseCloudflareStreamSource = (signedUrl?: string | null): CloudflareStreamSource | null => {
  if (!signedUrl) {
    return null;
  }

  try {
    const parsedUrl = new URL(signedUrl);
    const customerCodeMatch = /^customer-([^.]+)\.cloudflarestream\.com$/.exec(parsedUrl.hostname);
    const token = parsedUrl.pathname.split("/").find(Boolean);

    if (!customerCodeMatch?.[1] || !token) {
      return null;
    }

    return {
      customerCode: customerCodeMatch[1],
      token,
    };
  } catch {
    return null;
  }
};

export const shouldSuppressInitialAutoplay = ({
  suppressInitialAutoplay,
  slideId,
  initialAutoplaySlideId,
}: {
  suppressInitialAutoplay: boolean;
  slideId?: string | null;
  initialAutoplaySlideId?: string | null;
}): boolean => {
  return suppressInitialAutoplay && (slideId ?? null) === (initialAutoplaySlideId ?? null);
};

export const shouldLogPlayError = (error: unknown): boolean => {
  if (error === undefined || error === null) {
    return false;
  }

  return !isExpectedMediaPlaybackRejection(error);
};

export const attemptPlay = async ({
  player,
  onNotAllowed,
  onError,
}: {
  player: { play: () => Promise<void> | void };
  onNotAllowed?: () => void;
  onError?: (error: unknown) => void;
}): Promise<boolean> => {
  try {
    await player.play();
    return true;
  } catch (error) {
    onNotAllowed?.();
    if (shouldLogPlayError(error)) {
      onError?.(error);
    }
    return false;
  }
};
